import {FC, useState, useEffect, useCallback, useMemo, useRef} from 'react'
import {
  getPaymentsCalendar, getAccountTasks, createContactTask, setContactTaskDueDate, updateContactTask,
  deleteContactTask, resolveContactTask, reopenContactTask, addContactTaskNote,
  getTaskTemplates, saveTaskTemplates,
} from './_requests'
import type {CalendarTask, ContactTask, TaskProgress, TaskTemplate} from './_requests'
import {searchAccounts} from '../virtual-numbers/_requests'
import type {AccountSearchResult} from '../virtual-numbers/_requests'

// Payment tasks for /admin/payments: a month calendar of every task (drag a
// task to another day to move it), and a per-account window opened from the
// account cards. Every task belongs to an account. A task is new → in progress
// → done; "done" is the existing resolve, so the call list and history agree.

const TZ = 'Asia/Jerusalem'
const WEEKDAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳']
const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר']
const MAX_CHIPS = 4
const SHOW_DONE_KEY = 'payments-calendar-show-done'
// Live refresh: other admins (and other parts of this page) change tasks too.
const POLL_MS = 20000

type TaskState = TaskProgress | 'done'
const STATE_META: Record<TaskState, {label: string; color: string; bg: string; icon: string}> = {
  new: {label: 'חדש', color: '#ffa800', bg: '#fff8dd', icon: 'bi-circle'},
  in_progress: {label: 'בטיפול', color: '#3699ff', bg: '#eef6ff', icon: 'bi-hourglass-split'},
  done: {label: 'הושלם', color: '#50cd89', bg: '#e8fff3', icon: 'bi-check-circle-fill'},
}
const stateOf = (t: CalendarTask): TaskState => t.status === 'done' ? 'done' : (t.progress || 'new')

// A server timestamp -> its Israel calendar day, 'YYYY-MM-DD'.
const dayKey = (d: string | Date) => new Date(d).toLocaleDateString('en-CA', {timeZone: TZ})
const pad = (n: number) => String(n).padStart(2, '0')
const keyOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`
const fmt = (n: number) => `₪${(n || 0).toLocaleString('he-IL', {maximumFractionDigits: 0})}`
const prettyDay = (key: string) => {
  const [y, m, d] = key.split('-').map(Number)
  return `${d}/${m}/${y}`
}
const addDays = (key: string, n: number) => {
  const [y, m, d] = key.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + n))
  return keyOf(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate())
}
// "27/9 14:30" — a task's day (and time) for lists.
export const taskWhen = (t: {due_date?: string | null; due_time?: string | null}) =>
  t.due_date ? `${prettyDay(dayKey(t.due_date)).replace(/\/\d{4}$/, '')}${t.due_time ? ' ' + t.due_time : ''}` : 'ללא תאריך'

// Within a day: by time, untimed after timed.
const byTime = (a: CalendarTask, b: CalendarTask) => (a.due_time || '99:99').localeCompare(b.due_time || '99:99')
const taskName = (t: CalendarTask) =>
  `${t.account?.first_name || ''} ${t.account?.last_name || ''}`.trim() || t.account?.company_name || 'לקוח'

// The contact-tasks list route returns the full task; bring it to the calendar shape.
export const toCalendarTask = (t: ContactTask): CalendarTask => ({
  _id: t._id,
  account: t.account,
  status: t.status,
  progress: t.progress || 'new',
  reason: t.reason || '',
  source: t.source,
  due_date: t.due_date || null,
  due_time: t.due_time || null,
  notes_count: (t.notes || []).length,
  last_note: (t.notes || []).length ? t.notes[t.notes.length - 1].text : '',
  created_at: t.createdAt,
  resolved_at: t.resolved_at || null,
})

const btn = (bg: string, fg = '#fff', border = 'none'): React.CSSProperties => ({
  padding: '8px 14px', borderRadius: '8px', border, background: bg, color: fg,
  fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
})
const inputStyle: React.CSSProperties = {width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e4e6ef', fontSize: '13px'}

// Day + optional time, with one-tap היום / מחר / בעוד שבוע.
const WhenPicker: FC<{date: string; time: string; onDate: (d: string) => void; onTime: (t: string) => void}> = ({date, time, onDate, onTime}) => {
  const today = dayKey(new Date())
  const quick: [string, string][] = [['היום', today], ['מחר', addDays(today, 1)], ['בעוד שבוע', addDays(today, 7)]]
  return (
    <div>
      <div style={{display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap'}}>
        {quick.map(([label, key]) => (
          <button
            key={label}
            type="button"
            onClick={() => onDate(key)}
            style={{
              padding: '4px 10px', borderRadius: '14px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
              border: '1px solid #3699ff', background: date === key ? '#3699ff' : '#fff', color: date === key ? '#fff' : '#3699ff',
            }}
          >{label}</button>
        ))}
      </div>
      <div style={{display: 'flex', gap: '8px'}}>
        <input type="date" value={date} onChange={e => onDate(e.target.value)} style={{...inputStyle, flex: 1, width: 'auto', minWidth: '130px'}} />
        <input type="time" value={time} onChange={e => onTime(e.target.value)} disabled={!date} title="שעה (לא חובה)" style={{...inputStyle, width: '110px', opacity: date ? 1 : 0.5}} />
      </div>
    </div>
  )
}

export const REMINDER_OPTIONS: [number, string][] = [
  [0, 'בזמן המשימה'], [15, '15 דק׳ לפני'], [60, 'שעה לפני'], [1440, 'יום לפני'], [-1, 'ללא תזכורת'],
]
const DEFAULT_REMINDER = 15

const ReminderSelect: FC<{value: number; onChange: (v: number) => void; disabled?: boolean}> = ({value, onChange, disabled}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px'}}>
    <i className={`bi ${value === -1 ? 'bi-bell-slash' : 'bi-bell'}`} style={{color: value === -1 ? '#a1a5b7' : '#ffa800'}}></i>
    <select value={value} disabled={disabled} onChange={e => onChange(Number(e.target.value))} style={{...inputStyle, width: 'auto', padding: '5px 8px', fontSize: '12px'}}>
      {REMINDER_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
    <span style={{fontSize: '11px', color: '#a1a5b7'}}>ללא שעה = 09:00</span>
  </div>
)

// Shared task templates (all admins). Loaded once per page and kept in sync.
let templatesCache: TaskTemplate[] | null = null
const templateListeners = new Set<(t: TaskTemplate[]) => void>()
const publishTemplates = (t: TaskTemplate[]) => { templatesCache = t; templateListeners.forEach(fn => fn(t)) }
const useTaskTemplates = () => {
  const [templates, setTemplates] = useState<TaskTemplate[]>(templatesCache || [])
  useEffect(() => {
    templateListeners.add(setTemplates)
    if (!templatesCache) getTaskTemplates().then(r => publishTemplates(r.data.templates || [])).catch(() => {})
    return () => { templateListeners.delete(setTemplates) }
  }, [])
  return templates
}

// What a template fills into a new-task form.
type TemplateFill = {reason: string; date?: string; time?: string; reminder?: number}
const fillFromTemplate = (t: TaskTemplate): TemplateFill => ({
  reason: t.text,
  date: t.offset_days != null ? addDays(dayKey(new Date()), t.offset_days) : undefined,
  time: t.time || undefined,
  reminder: t.reminder != null ? t.reminder : undefined,
})
const templateHint = (t: TaskTemplate) => [
  t.offset_days != null ? (t.offset_days === 0 ? 'היום' : t.offset_days === 1 ? 'מחר' : `בעוד ${t.offset_days} ימים`) : '',
  t.time || '',
].filter(Boolean).join(' ')

const TemplatePicker: FC<{onPick: (f: TemplateFill) => void}> = ({onPick}) => {
  const templates = useTaskTemplates()
  const [managing, setManaging] = useState(false)
  return (
    <div style={{marginBottom: '8px'}}>
      <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center'}}>
        {templates.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => onPick(fillFromTemplate(t))}
            title={t.text}
            style={{
              padding: '4px 10px', borderRadius: '14px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
              border: '1px solid #8950fc', background: '#f8f5ff', color: '#6c3fd1', maxWidth: '220px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            <i className="bi bi-lightning-charge" style={{marginLeft: '3px'}}></i>
            {t.text}{templateHint(t) ? ` · ${templateHint(t)}` : ''}
          </button>
        ))}
        <button type="button" onClick={() => setManaging(true)} style={{border: 'none', background: 'none', color: '#8950fc', fontSize: '11px', fontWeight: 700, cursor: 'pointer', padding: '4px'}}>
          <i className="bi bi-gear" style={{marginLeft: '3px'}}></i>{templates.length ? 'ערוך תבניות' : 'הוסף תבניות'}
        </button>
      </div>
      {managing && <TemplatesManager onClose={() => setManaging(false)} />}
    </div>
  )
}

// Add / edit / delete / reorder the shared templates. Saves the whole list at once.
export const TemplatesManager: FC<{onClose: () => void}> = ({onClose}) => {
  const current = useTaskTemplates()
  const [rows, setRows] = useState<TaskTemplate[]>(() => current.map(t => ({...t})))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { if (!rows.length && current.length) setRows(current.map(t => ({...t}))) }, [current]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (i: number, patch: Partial<TaskTemplate>) => setRows(rs => rs.map((r, j) => j === i ? {...r, ...patch} : r))
  const move = (i: number, d: number) => setRows(rs => {
    const j = i + d
    if (j < 0 || j >= rs.length) return rs
    const next = [...rs]; [next[i], next[j]] = [next[j], next[i]]; return next
  })
  const add = () => setRows(rs => [...rs, {id: '', text: '', offset_days: null, time: null, reminder: null}])

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const res = await saveTaskTemplates(rows.map(r => ({...r, text: r.text.trim()})))
      publishTemplates(res.data.templates || [])
      onClose()
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה בשמירה')
    }
    setSaving(false)
  }

  return (
    <Popup
      title="תבניות משימה"
      onClose={onClose}
      z={1090}
      width={620}
      footer={<>
        <button style={btn('#fff', '#7e8299', '1px solid #e4e6ef')} onClick={onClose}>ביטול</button>
        <button style={btn('#8950fc')} disabled={saving} onClick={save}>{saving ? <span className="spinner-border spinner-border-sm" /> : 'שמור תבניות'}</button>
      </>}
    >
      <div style={{padding: '14px 20px'}}>
        <div style={{fontSize: '12px', color: '#7e8299', marginBottom: '10px'}}>
          משותף לכל האדמינים. לחיצה על תבנית בטופס משימה חדשה ממלאת את הטקסט, ואם הוגדרו — גם תאריך (בעוד X ימים), שעה ותזכורת.
        </div>
        {rows.length === 0 && <div style={{fontSize: '12px', color: '#a1a5b7', marginBottom: '10px'}}>אין תבניות עדיין</div>}
        {rows.map((r, i) => (
          <div key={r.id || `new-${i}`} style={{border: '1px solid #eff2f5', borderRadius: '10px', padding: '10px', marginBottom: '8px'}}>
            <div style={{display: 'flex', gap: '6px', alignItems: 'flex-start'}}>
              <textarea value={r.text} onChange={e => set(i, {text: e.target.value})} rows={2} placeholder="טקסט המשימה" style={{...inputStyle, flex: 1, resize: 'vertical'}} />
              <div style={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="למעלה" style={{border: 'none', background: 'none', cursor: 'pointer', color: '#7e8299', padding: '0 4px'}}><i className="bi bi-chevron-up"></i></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} title="למטה" style={{border: 'none', background: 'none', cursor: 'pointer', color: '#7e8299', padding: '0 4px'}}><i className="bi bi-chevron-down"></i></button>
                <button type="button" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} title="מחק" style={{border: 'none', background: 'none', cursor: 'pointer', color: '#f1416c', padding: '0 4px'}}><i className="bi bi-trash"></i></button>
              </div>
            </div>
            <div style={{display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center', fontSize: '12px'}}>
              <span>תאריך:</span>
              <select
                value={r.offset_days == null ? '' : String(r.offset_days)}
                onChange={e => set(i, {offset_days: e.target.value === '' ? null : Number(e.target.value)})}
                style={{...inputStyle, width: 'auto', padding: '5px 8px', fontSize: '12px'}}
              >
                <option value="">לפי מה שנבחר</option>
                <option value="0">היום</option>
                <option value="1">מחר</option>
                <option value="2">בעוד יומיים</option>
                <option value="3">בעוד 3 ימים</option>
                <option value="7">בעוד שבוע</option>
                <option value="14">בעוד שבועיים</option>
                <option value="30">בעוד חודש</option>
              </select>
              <span>שעה:</span>
              <input type="time" value={r.time || ''} onChange={e => set(i, {time: e.target.value || null})} style={{...inputStyle, width: '110px', padding: '5px 8px', fontSize: '12px'}} />
              <span>תזכורת:</span>
              <select
                value={r.reminder == null ? '' : String(r.reminder)}
                onChange={e => set(i, {reminder: e.target.value === '' ? null : Number(e.target.value)})}
                style={{...inputStyle, width: 'auto', padding: '5px 8px', fontSize: '12px'}}
              >
                <option value="">ברירת מחדל</option>
                {REMINDER_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
          </div>
        ))}
        <button style={btn('#fff', '#8950fc', '1px dashed #8950fc')} onClick={add}><i className="bi bi-plus-lg"></i>תבנית חדשה</button>
        {error && <div style={{fontSize: '12px', color: '#f1416c', marginTop: '8px'}}>{error}</div>}
      </div>
    </Popup>
  )
}

// The next open tasks, shown right on an account card. Any row opens the full window.
export const AccountTaskPreview: FC<{
  tasks: {_id: string; reason?: string; due_date?: string | null; due_time?: string | null; progress?: TaskProgress}[]
  total: number
  onOpen: () => void
}> = ({tasks, total, onOpen}) => {
  if (!tasks.length) return null
  const today = dayKey(new Date())
  return (
    <div style={{border: '1px solid #e1efff', background: '#f8fbff', borderRadius: '8px', padding: '6px 8px', marginBottom: '8px'}}>
      {tasks.map(t => {
        const m = STATE_META[t.progress || 'new']
        const late = !!t.due_date && dayKey(t.due_date) < today
        return (
          <div key={t._id} onClick={onOpen} style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '3px 0', cursor: 'pointer'}}>
            <i className={`bi ${m.icon}`} style={{color: m.color, fontSize: '10px'}}></i>
            <span style={{fontWeight: 700, color: late ? '#f1416c' : '#5e6278', whiteSpace: 'nowrap'}}>{taskWhen(t)}</span>
            <span style={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1e1e2d'}}>{t.reason || 'ללא תיאור'}</span>
          </div>
        )
      })}
      {total > tasks.length && (
        <div onClick={onOpen} style={{fontSize: '11px', color: '#3699ff', fontWeight: 600, cursor: 'pointer', paddingTop: '2px'}}>+{total - tasks.length} נוספות</div>
      )}
    </div>
  )
}

// ============================================================================
// Calendar
// ============================================================================

export const PaymentsCalendar: FC<{
  isMobile: boolean
  onClose: () => void
  onOpenAccount: (query: string) => void
  // Called after any task change so the rest of the page (call list, cards) stays in sync.
  onTasksChanged?: () => void
  // Bumped by the page whenever a task changes elsewhere on it — reload right away.
  refreshKey?: number
}> = ({isMobile, onClose, onOpenAccount, onTasksChanged, refreshKey}) => {
  const today = dayKey(new Date())
  const [cursor, setCursor] = useState(() => {
    const [y, m] = today.split('-').map(Number)
    return {y, m: m - 1}
  })
  const [tasks, setTasks] = useState<CalendarTask[]>([])
  // Today..today+7, fetched on its own so the shortcuts work whatever month is shown.
  const [upcoming, setUpcoming] = useState<CalendarTask[]>([])
  const [rangeOpen, setRangeOpen] = useState<'today' | 'tomorrow' | 'week' | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // Completed tasks are shown (faded) unless turned off; remembered per browser.
  const [showDone, setShowDone] = useState<boolean>(() => {
    try { return window.localStorage.getItem(SHOW_DONE_KEY) !== '0' } catch (e) { return true }
  })
  useEffect(() => {
    try { window.localStorage.setItem(SHOW_DONE_KEY, showDone ? '1' : '0') } catch (e) {}
  }, [showDone])
  const [dragId, setDragId] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dayOpen, setDayOpen] = useState<string | null>(null)
  const [addTaskDay, setAddTaskDay] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [templatesOpen, setTemplatesOpen] = useState(false)

  // The grid shows whole weeks (Sun–Sat), so it spills into the neighbouring months.
  const grid = useMemo(() => {
    const first = new Date(Date.UTC(cursor.y, cursor.m, 1))
    const start = new Date(first)
    start.setUTCDate(1 - first.getUTCDay())
    const last = new Date(Date.UTC(cursor.y, cursor.m + 1, 0))
    const end = new Date(last)
    end.setUTCDate(last.getUTCDate() + (6 - last.getUTCDay()))
    const days: {key: string; day: number; inMonth: boolean}[] = []
    for (const d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      days.push({key: keyOf(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()), day: d.getUTCDate(), inMonth: d.getUTCMonth() === cursor.m})
    }
    return days
  }, [cursor])

  // quiet = a background refresh: no spinner, and a failure keeps what's shown.
  const load = useCallback(async (quiet = false) => {
    if (!quiet) { setLoading(true); setError('') }
    try {
      const now = dayKey(new Date())
      const [res, up] = await Promise.all([
        getPaymentsCalendar(grid[0].key, grid[grid.length - 1].key),
        getPaymentsCalendar(now, addDays(now, 7)),
      ])
      setTasks(res.data.tasks || [])
      setUpcoming((up.data.tasks || []).filter(t => t.due_date))
    } catch (e: any) {
      if (!quiet) setError(e.response?.data?.error || 'שגיאה בטעינת לוח השנה')
    }
    if (!quiet) setLoading(false)
  }, [grid])

  useEffect(() => { load() }, [load])

  // Live: poll, refetch when the tab comes back, and when the page says so.
  // Skipped mid-drag so a refresh never yanks a chip out from under the cursor.
  const dragRef = useRef<string | null>(null)
  dragRef.current = dragId
  useEffect(() => {
    const tick = () => { if (!dragRef.current && document.visibilityState === 'visible') load(true) }
    const timer = window.setInterval(tick, POLL_MS)
    document.addEventListener('visibilitychange', tick)
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', tick) }
  }, [load])
  const firstRefresh = useRef(true)
  useEffect(() => {
    if (firstRefresh.current) { firstRefresh.current = false; return }
    load(true)
  }, [refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const byDay = useMemo(() => {
    const map: Record<string, CalendarTask[]> = {}
    for (const t of tasks) {
      if (!t.due_date || (!showDone && t.status === 'done')) continue
      const k = dayKey(t.due_date)
      ;(map[k] = map[k] || []).push(t)
    }
    // Open by time, done last.
    for (const k of Object.keys(map)) map[k].sort((a, b) => (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0) || byTime(a, b))
    return map
  }, [tasks, showDone])

  const unscheduled = tasks.filter(t => !t.due_date && t.status === 'open')

  const ranges = useMemo(() => {
    const open = upcoming.filter(t => t.status === 'open' && t.due_date)
    const inRange = (from: number, to: number) => open
      .filter(t => { const k = dayKey(t.due_date as string); return k >= addDays(today, from) && k <= addDays(today, to) })
      .sort((a, b) => dayKey(a.due_date as string).localeCompare(dayKey(b.due_date as string)) || byTime(a, b))
    return {today: inRange(0, 0), tomorrow: inRange(1, 1), week: inRange(0, 7)}
  }, [upcoming, today])
  const RANGE_LABEL = {today: 'היום', tomorrow: 'מחר', week: '7 ימים קרובים'} as const
  const selected = tasks.find(t => t._id === selectedId) || upcoming.find(t => t._id === selectedId) || null
  // Apply one change to both lists (grid month + the upcoming shortcuts).
  const mutate = (fn: (ts: CalendarTask[]) => CalendarTask[]) => { setTasks(fn); setUpcoming(fn) }

  const monthCounts = useMemo(() => {
    const prefix = `${cursor.y}-${pad(cursor.m + 1)}`
    const inMonth = tasks.filter(t => t.due_date && dayKey(t.due_date).startsWith(prefix))
    return {
      open: inMonth.filter(t => t.status === 'open').length,
      done: inMonth.filter(t => t.status === 'done').length,
      overdue: tasks.filter(t => t.status === 'open' && t.due_date && dayKey(t.due_date) < today).length,
    }
  }, [tasks, cursor, today])

  const patchTask = (updated: CalendarTask) => {
    mutate(ts => ts.map(t => t._id === updated._id ? updated : t))
    onTasksChanged?.()
  }

  const moveTask = async (task: CalendarTask, toKey: string | null) => {
    if ((task.due_date ? dayKey(task.due_date) : null) === toKey) return
    const prev = tasks
    setTasks(ts => ts.map(t => t._id === task._id ? {...t, due_date: toKey ? `${toKey}T12:00:00+03:00` : null} : t))
    setBusy(task._id)
    try {
      const res = await setContactTaskDueDate(task._id, toKey)
      mutate(ts => ts.map(t => t._id === task._id ? {...t, due_date: res.data.due_date, due_time: res.data.due_time} : t))
      load(true) // the upcoming shortcuts may gain or lose it
      onTasksChanged?.()
    } catch (e: any) {
      setTasks(prev)
      setError(e.response?.data?.error || 'שגיאה בהזזת המשימה')
    }
    setBusy(null)
  }

  const onDrop = (toKey: string | null) => {
    const id = dragId
    setDragId(null)
    setOverKey(null)
    const t = tasks.find(x => x._id === id)
    if (t) moveTask(t, toKey)
  }

  const openAddTask = (key: string) => {
    setDayOpen(null)
    setAddTaskDay(key)
  }

  // Chip: time + client on the first line, the task text under it (2 lines max).
  const renderChip = (t: CalendarTask) => {
    const st = stateOf(t)
    const meta = STATE_META[st]
    return (
      <div
        key={t._id}
        draggable={!isMobile}
        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragId(t._id) }}
        onDragEnd={() => { setDragId(null); setOverKey(null) }}
        onClick={e => { e.stopPropagation(); setSelectedId(t._id) }}
        title={`${t.due_time ? t.due_time + ' · ' : ''}${taskName(t)} · ${meta.label}${t.reason ? ' — ' + t.reason : ''}`}
        style={{
          padding: '3px 6px', borderRadius: '5px', fontSize: '11px', lineHeight: '15px',
          cursor: 'pointer', marginBottom: '3px', userSelect: 'none', overflow: 'hidden',
          background: meta.bg, borderRight: `3px solid ${meta.color}`, color: st === 'done' ? '#7e8299' : '#1e1e2d',
          opacity: busy === t._id ? 0.5 : st === 'done' ? 0.75 : 1,
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textDecoration: st === 'done' ? 'line-through' : 'none'}}>
          <i className={`bi ${meta.icon}`} style={{fontSize: '9px', color: meta.color}}></i>
          {t.due_time && <span style={{fontWeight: 700, color: meta.color}}>{t.due_time}</span>}
          <span style={{fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis'}}>{taskName(t)}</span>
        </div>
        {t.reason && (
          <div style={{
            color: '#5e6278', fontSize: '10.5px', marginTop: '1px', whiteSpace: 'normal', wordBreak: 'break-word',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{t.reason}</div>
        )}
      </div>
    )
  }

  const navBtn: React.CSSProperties = {
    padding: '6px 10px', borderRadius: '8px', border: '1px solid #e4e6ef', background: '#fff',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: '#3f4254',
  }

  return (
    <div className="pc-root" style={{background: '#fff', borderRadius: '12px', border: '1px solid #e4e6ef', padding: isMobile ? '12px' : '16px', marginBottom: '20px', direction: 'rtl'}}>
      <style>{`.pc-root .pc-add-task{opacity:0;transition:opacity .15s}.pc-root .pc-day:hover .pc-add-task{opacity:1}`}</style>
      {/* Header: month nav + counts + actions */}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '12px'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
          <button style={navBtn} onClick={() => setCursor(c => c.m === 0 ? {y: c.y - 1, m: 11} : {y: c.y, m: c.m - 1})} title="חודש קודם">
            <i className="bi bi-chevron-right"></i>
          </button>
          <h3 style={{margin: 0, fontSize: '17px', fontWeight: 700, minWidth: '120px', textAlign: 'center'}}>
            {MONTHS[cursor.m]} {cursor.y}
          </h3>
          <button style={navBtn} onClick={() => setCursor(c => c.m === 11 ? {y: c.y + 1, m: 0} : {y: c.y, m: c.m + 1})} title="חודש הבא">
            <i className="bi bi-chevron-left"></i>
          </button>
          <button style={navBtn} onClick={() => { const [y, m] = today.split('-').map(Number); setCursor({y, m: m - 1}) }}>היום</button>
          {loading && <span className="spinner-border spinner-border-sm" style={{color: '#3699ff'}} />}
        </div>
        <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
          {(['today', 'tomorrow', 'week'] as const).map(r => (
            <button
              key={r}
              onClick={() => setRangeOpen(r)}
              style={{
                padding: '5px 10px', borderRadius: '14px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                border: '1px solid #3699ff', background: ranges[r].length ? '#3699ff' : '#fff', color: ranges[r].length ? '#fff' : '#3699ff',
              }}
            >
              {r === 'week' ? 'השבוע' : RANGE_LABEL[r]} ({ranges[r].length})
            </button>
          ))}
        </div>
        <div style={{display: 'flex', gap: '12px', fontSize: '12px', color: '#5e6278', flexWrap: 'wrap'}}>
          <span>פתוחות החודש: <b style={{color: '#ffa800'}}>{monthCounts.open}</b></span>
          <span>הושלמו: <b style={{color: '#50cd89'}}>{monthCounts.done}</b></span>
          {monthCounts.overdue > 0 && <span>באיחור: <b style={{color: '#f1416c'}}>{monthCounts.overdue}</b></span>}
        </div>
        <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
          <button style={btn('#ffa800')} onClick={() => openAddTask(today)}><i className="bi bi-plus-lg"></i>משימה</button>
          <button style={btn('#fff', '#8950fc', '1px solid #8950fc')} onClick={() => setTemplatesOpen(true)} title="תבניות משימה קבועות"><i className="bi bi-lightning-charge"></i>תבניות</button>
          <button
            onClick={() => setShowDone(v => !v)}
            style={{padding: '5px 10px', borderRadius: '14px', border: '1px solid #50cd89', fontSize: '11px', fontWeight: 700, background: showDone ? '#50cd89' : '#fff', color: showDone ? '#fff' : '#50cd89', cursor: 'pointer'}}
          >
            הצג שהושלמו
          </button>
          <button onClick={onClose} title="סגור" style={{border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#a1a5b7'}}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      {error && (
        <div style={{background: '#fff5f8', color: '#f1416c', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between'}}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{border: 'none', background: 'none', color: '#f1416c', cursor: 'pointer'}}><i className="bi bi-x"></i></button>
        </div>
      )}

      <div style={{display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start'}}>
        {/* Month grid */}
        <div style={{flex: 1, minWidth: 0, width: '100%'}}>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '4px', marginBottom: '4px'}}>
            {WEEKDAYS.map(w => (
              <div key={w} style={{textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#a1a5b7'}}>{w}</div>
            ))}
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '4px'}}>
            {grid.map(({key, day, inMonth}) => {
              const list = byDay[key] || []
              const isToday = key === today
              const isOver = overKey === key && !!dragId
              const overdue = key < today && list.some(t => t.status === 'open')
              return (
                <div
                  key={key}
                  className="pc-day"
                  onDragOver={e => { if (dragId) { e.preventDefault(); if (overKey !== key) setOverKey(key) } }}
                  onDragLeave={() => { if (overKey === key) setOverKey(null) }}
                  onDrop={e => { e.preventDefault(); onDrop(key) }}
                  onClick={() => setDayOpen(key)}
                  style={{
                    minHeight: isMobile ? '60px' : '104px', borderRadius: '8px', padding: '4px',
                    background: isOver ? '#e8f4fd' : inMonth ? '#fff' : '#f9f9fb',
                    border: isOver ? '2px dashed #3699ff' : isToday ? '2px solid #3699ff' : overdue ? '1px solid #f8c4d0' : '1px solid #eff2f5',
                    opacity: inMonth ? 1 : 0.6, cursor: 'pointer', overflow: 'hidden',
                  }}
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px'}}>
                    <span style={{fontSize: '12px', fontWeight: isToday ? 800 : 600, color: isToday ? '#3699ff' : '#3f4254'}}>{day}</span>
                    {!isMobile && (
                      <button
                        className="pc-add-task"
                        title="הוסף משימה ליום זה"
                        onClick={e => { e.stopPropagation(); openAddTask(key) }}
                        style={{border: 'none', background: 'none', padding: 0, color: '#ffa800', cursor: 'pointer', fontSize: '12px', lineHeight: 1}}
                      >
                        <i className="bi bi-plus-circle"></i>
                      </button>
                    )}
                  </div>
                  {isMobile ? (
                    list.length > 0 && (
                      <div style={{display: 'flex', gap: '2px', flexWrap: 'wrap'}}>
                        {(['new', 'in_progress', 'done'] as TaskState[]).map(st => {
                          const n = list.filter(t => stateOf(t) === st).length
                          return n ? <span key={st} style={{fontSize: '10px', background: STATE_META[st].color, color: '#fff', borderRadius: '6px', padding: '0 4px'}}>{n}</span> : null
                        })}
                      </div>
                    )
                  ) : (
                    <>
                      {list.slice(0, MAX_CHIPS).map(renderChip)}
                      {list.length > MAX_CHIPS && (
                        <div style={{fontSize: '11px', color: '#3699ff', fontWeight: 600, padding: '0 4px'}}>+{list.length - MAX_CHIPS} נוספות</div>
                      )}
                    </>
                  )}
                </div>
              )
            })}
          </div>
          <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '11px', color: '#7e8299', marginTop: '8px'}}>
            {(['new', 'in_progress', 'done'] as TaskState[]).map(st => (
              <span key={st}><i className={`bi ${STATE_META[st].icon}`} style={{color: STATE_META[st].color}}></i> {STATE_META[st].label}</span>
            ))}
            {!isMobile && <span>גרור משימה ליום אחר כדי להזיז אותה</span>}
          </div>
        </div>

        {/* Unscheduled tasks — drag onto a day; drop a dated task here to clear its date. */}
        <div
          onDragOver={e => { if (dragId) { e.preventDefault(); setOverKey('__unscheduled') } }}
          onDragLeave={() => { if (overKey === '__unscheduled') setOverKey(null) }}
          onDrop={e => { e.preventDefault(); onDrop(null) }}
          style={{
            width: isMobile ? '100%' : '220px', flexShrink: 0, borderRadius: '10px', padding: '10px',
            background: overKey === '__unscheduled' ? '#fff8dd' : '#f9f9fb',
            border: overKey === '__unscheduled' ? '2px dashed #ffa800' : '1px solid #eff2f5',
            maxHeight: isMobile ? '240px' : '720px', overflowY: 'auto',
          }}
        >
          <div style={{fontSize: '12px', fontWeight: 700, color: '#3f4254', marginBottom: '8px'}}>
            <i className="bi bi-inbox" style={{marginLeft: '6px', color: '#ffa800'}}></i>
            ללא תאריך ({unscheduled.length})
          </div>
          {unscheduled.length === 0 ? (
            <div style={{fontSize: '11px', color: '#a1a5b7'}}>כל המשימות מתוזמנות</div>
          ) : unscheduled.map(renderChip)}
        </div>
      </div>

      {/* Everything on one day */}
      {dayOpen && (
        <Popup title={prettyDay(dayOpen)} onClose={() => setDayOpen(null)}>
          <div style={{padding: '14px 20px'}}>
            <button style={{...btn('#ffa800'), marginBottom: '14px'}} onClick={() => openAddTask(dayOpen)}>
              <i className="bi bi-plus-lg"></i>הוסף משימה ליום זה
            </button>
            {(byDay[dayOpen] || []).length === 0
              ? <div style={{fontSize: '12px', color: '#a1a5b7'}}>אין משימות ביום הזה</div>
              : (byDay[dayOpen] || []).map(renderChip)}
          </div>
        </Popup>
      )}

      {rangeOpen && (
        <Popup title={`משימות פתוחות · ${RANGE_LABEL[rangeOpen]}`} onClose={() => setRangeOpen(null)}>
          <div style={{padding: '14px 20px'}}>
            {ranges[rangeOpen].length === 0 ? (
              <div style={{fontSize: '12px', color: '#a1a5b7'}}>אין משימות פתוחות</div>
            ) : ranges[rangeOpen].map(t => {
              const m = STATE_META[stateOf(t)]
              return (
                <div key={t._id} onClick={() => setSelectedId(t._id)} style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #eff2f5', marginBottom: '6px', cursor: 'pointer', background: m.bg}}>
                  <i className={`bi ${m.icon}`} style={{color: m.color}}></i>
                  <span style={{fontSize: '12px', fontWeight: 700, color: '#5e6278', whiteSpace: 'nowrap'}}>{taskWhen(t)}</span>
                  <span style={{fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap'}}>{taskName(t)}</span>
                  <span style={{flex: 1, fontSize: '12px', color: '#7e8299', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{t.reason}</span>
                </div>
              )
            })}
          </div>
        </Popup>
      )}

      {selected && (
        <Popup title={taskName(selected)} onClose={() => setSelectedId(null)} z={1070}>
          <TaskEditor
            key={selected._id}
            task={selected}
            onChanged={patchTask}
            onDeleted={id => { mutate(ts => ts.filter(t => t._id !== id)); setSelectedId(null); onTasksChanged?.() }}
            onOpenAccount={q => { setSelectedId(null); setDayOpen(null); onOpenAccount(q) }}
          />
        </Popup>
      )}

      {templatesOpen && <TemplatesManager onClose={() => setTemplatesOpen(false)} />}

      {addTaskDay && (
        <AddTaskModal
          day={addTaskDay}
          onClose={() => setAddTaskDay(null)}
          onCreated={() => { setAddTaskDay(null); load(); onTasksChanged?.() }}
        />
      )}
    </div>
  )
}

// ============================================================================
// One task: status (new / in progress / done), progress notes, day, text, delete.
// Shared by the calendar popup and the per-account window.
// ============================================================================

export const TaskEditor: FC<{
  task: CalendarTask
  onChanged: (t: CalendarTask) => void
  onDeleted: (taskId: string) => void
  onOpenAccount?: (query: string) => void
}> = ({task, onChanged, onDeleted, onOpenAccount}) => {
  const [reason, setReason] = useState(task.reason || '')
  const [date, setDate] = useState(task.due_date ? dayKey(task.due_date) : '')
  const [time, setTime] = useState(task.due_time || '')
  const [note, setNote] = useState('')
  const reminder = task.reminder == null ? DEFAULT_REMINDER : task.reminder
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const st = stateOf(task)

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError('')
    try { await fn() } catch (e: any) { setError(e.response?.data?.error || 'שגיאה') }
    setBusy(false)
  }

  const setState = (to: TaskState) => run(async () => {
    if (to === st) return
    if (to === 'done') {
      await resolveContactTask(task._id)
      onChanged({...task, status: 'done', resolved_at: new Date().toISOString()})
      return
    }
    if (task.status === 'done') await reopenContactTask(task._id)
    const res = await updateContactTask(task._id, {progress: to})
    onChanged({...task, status: 'open', progress: res.data.progress, resolved_at: null})
  })

  const saveReason = () => run(async () => {
    const res = await updateContactTask(task._id, {reason})
    onChanged({...task, reason: res.data.reason})
  })

  const saveDate = (to: string | null) => run(async () => {
    if (!to) {
      const res = await setContactTaskDueDate(task._id, null)
      onChanged({...task, due_date: res.data.due_date, due_time: null})
      setDate(''); setTime('')
      return
    }
    const res = await updateContactTask(task._id, {due_date: to, due_time: time || null})
    onChanged({...task, due_date: res.data.due_date, due_time: res.data.due_time})
  })
  const whenChanged = date !== (task.due_date ? dayKey(task.due_date) : '') || time !== (task.due_time || '')

  const saveReminder = (v: number) => run(async () => {
    const res = await updateContactTask(task._id, {reminder: v})
    onChanged({...task, reminder: res.data.reminder})
  })

  const addNote = () => run(async () => {
    const text = note.trim()
    if (!text) return
    await addContactTaskNote(task._id, text)
    setNote('')
    onChanged({...task, notes_count: task.notes_count + 1, last_note: text})
  })

  const remove = () => run(async () => {
    await deleteContactTask(task._id)
    onDeleted(task._id)
  })

  const label = (text: string) => <div style={{fontSize: '12px', fontWeight: 700, margin: '12px 0 6px'}}>{text}</div>

  return (
    <div style={{padding: '14px 20px'}}>
      {/* Status */}
      <div style={{display: 'flex', gap: '6px'}}>
        {(['new', 'in_progress', 'done'] as TaskState[]).map(s => {
          const m = STATE_META[s]
          const on = s === st
          return (
            <button
              key={s}
              disabled={busy}
              onClick={() => setState(s)}
              style={{
                flex: 1, padding: '8px 6px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: busy ? 'default' : 'pointer',
                border: `1px solid ${m.color}`, background: on ? m.color : '#fff', color: on ? '#fff' : m.color,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              }}
            >
              <i className={`bi ${m.icon}`}></i>{s === 'done' && !on ? 'סמן כהושלם' : m.label}
            </button>
          )
        })}
      </div>

      {label('מה צריך לעשות')}
      <textarea
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={3}
        placeholder="למשל: לחייב שנתי, לבקש כרטיס חדש..."
        style={{...inputStyle, border: '1px solid #ffd666', background: '#fffdf5', resize: 'vertical'}}
      />
      {reason.trim() !== (task.reason || '').trim() && (
        <div style={{display: 'flex', gap: '8px', marginTop: '6px'}}>
          <button style={btn('#ffa800')} disabled={busy} onClick={saveReason}>שמור</button>
          <button style={btn('#fff', '#7e8299', '1px solid #e4e6ef')} onClick={() => setReason(task.reason || '')}>בטל שינוי</button>
        </div>
      )}

      {label('תאריך ושעה')}
      <WhenPicker date={date} time={time} onDate={setDate} onTime={setTime} />
      <ReminderSelect value={reminder} onChange={saveReminder} disabled={busy} />
      <div style={{display: 'flex', gap: '8px', marginTop: '8px'}}>
        <button style={btn('#3699ff')} disabled={busy || !date || !whenChanged} onClick={() => saveDate(date)}>קבע</button>
        {task.due_date && <button style={btn('#fff', '#7e8299', '1px solid #e4e6ef')} disabled={busy} onClick={() => saveDate(null)}>הסר תאריך</button>}
      </div>

      {label(`התקדמות${task.notes_count ? ` (${task.notes_count})` : ''}`)}
      {task.last_note && <div style={{fontSize: '12px', color: '#5e6278', background: '#f9f9fb', padding: '6px 10px', borderRadius: '8px', marginBottom: '6px'}}>אחרונה: {task.last_note}</div>}
      <div style={{display: 'flex', gap: '8px'}}>
        <input
          value={note}
          onChange={e => setNote(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addNote() }}
          placeholder="מה קרה? (דיברתי איתו, ביקש לחזור מחר...)"
          style={{...inputStyle, flex: 1, width: 'auto'}}
        />
        <button style={btn('#3699ff')} disabled={busy || !note.trim()} onClick={addNote}>הוסף</button>
      </div>

      <div style={{fontSize: '11px', color: '#a1a5b7', marginTop: '10px'}}>
        נוצרה {prettyDay(dayKey(task.created_at))}
        {task.account?.phone && <> · <a href={`tel:${task.account.phone}`} style={{direction: 'ltr'}}>{task.account.phone}</a></>}
        {task.account?.monthly_payment_amount ? ` · ${fmt(task.account.monthly_payment_amount)} לחודש` : ''}
      </div>

      {error && <div style={{fontSize: '12px', color: '#f1416c', marginTop: '8px'}}>{error}</div>}

      <div style={{display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap', justifyContent: 'space-between'}}>
        {onOpenAccount
          ? <button style={btn('#fff', '#3699ff', '1px solid #3699ff')} onClick={() => onOpenAccount(task.account?.phone || taskName(task))}><i className="bi bi-search"></i>הצג בכרטיס לקוח</button>
          : <span />}
        {confirmDelete ? (
          <span style={{display: 'inline-flex', gap: '6px', alignItems: 'center'}}>
            <span style={{fontSize: '12px', color: '#f1416c'}}>למחוק את המשימה?</span>
            <button style={btn('#f1416c')} disabled={busy} onClick={remove}>מחק</button>
            <button style={btn('#fff', '#7e8299', '1px solid #e4e6ef')} onClick={() => setConfirmDelete(false)}>לא</button>
          </span>
        ) : (
          <button style={btn('#fff', '#f1416c', '1px solid #f1416c')} onClick={() => setConfirmDelete(true)}><i className="bi bi-trash"></i>מחק</button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// New task. With `account` the client is fixed (per-account window); otherwise
// the admin searches for one — a task can't be saved without a client.
// ============================================================================

const AddTaskModal: FC<{
  day?: string
  account?: {_id: string; name: string}
  onClose: () => void
  onCreated: (task: ContactTask) => void
}> = ({day, account, onClose, onCreated}) => {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<AccountSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [picked, setPicked] = useState<{_id: string; name: string; phone?: string} | null>(account || null)
  const [date, setDate] = useState(day || dayKey(new Date()))
  const [time, setTime] = useState('')
  const [reminder, setReminder] = useState(DEFAULT_REMINDER)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const applyTemplate = (f: TemplateFill) => {
    setReason(f.reason)
    if (f.date) setDate(f.date)
    if (f.time !== undefined) setTime(f.time)
    if (f.reminder !== undefined) setReminder(f.reminder)
  }

  useEffect(() => {
    const term = q.trim()
    if (picked || term.length < 2) { setResults([]); return }
    let alive = true
    const t = window.setTimeout(async () => {
      setSearching(true)
      try {
        const res = await searchAccounts(term)
        if (alive) setResults(res.data.accounts || [])
      } catch (e) { if (alive) setResults([]) }
      if (alive) setSearching(false)
    }, 350)
    return () => { alive = false; window.clearTimeout(t) }
  }, [q, picked])

  const acctName = (a: AccountSearchResult) => `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.company_name || a.email || ''

  const save = async () => {
    if (!picked) { setError('יש לבחור לקוח'); return }
    setSaving(true)
    setError('')
    try {
      const res = await createContactTask(picked._id, {reason: reason.trim() || undefined, due_date: date || undefined, due_time: (date && time) || undefined, reminder})
      onCreated(res.data.task)
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה ביצירת המשימה')
      setSaving(false)
    }
  }

  return (
    <Popup
      title={account ? `משימה חדשה · ${account.name}` : 'משימה חדשה'}
      onClose={onClose}
      z={1060}
      footer={<>
        <button style={btn('#fff', '#7e8299', '1px solid #e4e6ef')} onClick={onClose}>ביטול</button>
        <button style={btn('#ffa800')} disabled={saving || !picked} onClick={save}>
          {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-plus-lg"></i>הוסף</>}
        </button>
      </>}
    >
      <div style={{padding: '14px 20px'}}>
        {!account && (
          <>
            <label style={{fontSize: '12px', fontWeight: 700}}>לקוח</label>
            {picked ? (
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', background: '#f1faff', border: '1px solid #3699ff', margin: '4px 0 12px'}}>
                <span style={{fontSize: '13px', fontWeight: 600}}>{picked.name} {picked.phone && <span style={{color: '#7e8299', fontWeight: 400}}>· {picked.phone}</span>}</span>
                <button onClick={() => { setPicked(null); setQ('') }} style={{border: 'none', background: 'none', color: '#7e8299', cursor: 'pointer'}}><i className="bi bi-x-lg"></i></button>
              </div>
            ) : (
              <div style={{position: 'relative', margin: '4px 0 12px'}}>
                <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="חפש לפי שם, טלפון או אימייל" style={inputStyle} />
                {searching && <span className="spinner-border spinner-border-sm" style={{position: 'absolute', left: '10px', top: '10px', color: '#3699ff'}} />}
                {results.length > 0 && (
                  <div style={{border: '1px solid #e4e6ef', borderRadius: '8px', marginTop: '4px', maxHeight: '160px', overflowY: 'auto'}}>
                    {results.map(a => (
                      <div key={a._id} onClick={() => { setPicked({_id: a._id, name: acctName(a), phone: a.phone}); setResults([]) }} style={{padding: '7px 10px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid #f5f8fa'}}>
                        <b>{acctName(a)}</b>{a.phone && <span style={{color: '#7e8299', marginRight: '6px'}}>{a.phone}</span>}
                        {a.email && <div style={{fontSize: '11px', color: '#a1a5b7'}}>{a.email}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {!searching && q.trim().length >= 2 && results.length === 0 && (
                  <div style={{fontSize: '11px', color: '#a1a5b7', marginTop: '4px'}}>לא נמצאו לקוחות</div>
                )}
              </div>
            )}
          </>
        )}
        <label style={{fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px'}}>תבנית</label>
        <TemplatePicker onPick={applyTemplate} />
        <label style={{fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px'}}>תאריך ושעה</label>
        <div style={{marginBottom: '12px'}}>
          <WhenPicker date={date} time={time} onDate={setDate} onTime={setTime} />
          <ReminderSelect value={reminder} onChange={setReminder} />
        </div>
        <label style={{fontSize: '12px', fontWeight: 700}}>מה צריך לעשות</label>
        <textarea autoFocus={!!account} value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="למשל: לחייב שנתי, לבקש כרטיס חדש..." style={{...inputStyle, marginTop: '4px', resize: 'vertical'}} />
        {error && <div style={{fontSize: '12px', color: '#f1416c', marginTop: '8px'}}>{error}</div>}
      </div>
    </Popup>
  )
}

// Inline "new task" for one client — sits at the top of the account window.
const NewTaskInline: FC<{accountId: string; onCreated: (task: ContactTask) => void}> = ({accountId, onCreated}) => {
  const [date, setDate] = useState(dayKey(new Date()))
  const [time, setTime] = useState('')
  const [reminder, setReminder] = useState(DEFAULT_REMINDER)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const applyTemplate = (f: TemplateFill) => {
    setReason(f.reason)
    if (f.date) setDate(f.date)
    if (f.time !== undefined) setTime(f.time)
    if (f.reminder !== undefined) setReminder(f.reminder)
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const res = await createContactTask(accountId, {reason: reason.trim() || undefined, due_date: date || undefined, due_time: (date && time) || undefined, reminder})
      onCreated(res.data.task)
      setReason(''); setTime(''); setReminder(DEFAULT_REMINDER)
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה ביצירת המשימה')
    }
    setSaving(false)
  }

  return (
    <div style={{border: '1px solid #ffd666', background: '#fffdf5', borderRadius: '10px', padding: '12px', marginBottom: '14px'}}>
      <div style={{fontSize: '12px', fontWeight: 700, marginBottom: '6px'}}><i className="bi bi-plus-circle" style={{color: '#ffa800', marginLeft: '6px'}}></i>משימה חדשה</div>
      <TemplatePicker onPick={applyTemplate} />
      <textarea
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={2}
        placeholder="מה צריך לעשות? (לחייב שנתי, לבקש כרטיס חדש...)"
        style={{...inputStyle, resize: 'vertical', background: '#fff', marginBottom: '8px'}}
      />
      <WhenPicker date={date} time={time} onDate={setDate} onTime={setTime} />
      <ReminderSelect value={reminder} onChange={setReminder} />
      {error && <div style={{fontSize: '12px', color: '#f1416c', marginTop: '6px'}}>{error}</div>}
      <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '8px'}}>
        <button style={btn('#ffa800')} disabled={saving} onClick={save}>
          {saving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-plus-lg"></i>הוסף משימה</>}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Per-account window, opened from an account card: all of that client's tasks.
// ============================================================================

export const AccountTasksModal: FC<{
  account: {_id: string; first_name?: string; last_name?: string; company_name?: string}
  onClose: () => void
  // Fired after every change with the account's OPEN tasks, so the card can update.
  onChanged: (openTasks: CalendarTask[]) => void
}> = ({account, onClose, onChanged}) => {
  const name = `${account.first_name || ''} ${account.last_name || ''}`.trim() || account.company_name || 'לקוח'
  const [tasks, setTasks] = useState<CalendarTask[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [showDone, setShowDone] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getAccountTasks(account._id)
      setTasks((res.data.tasks || []).map(toCalendarTask))
    } catch (e: any) {
      setError(e.response?.data?.error || 'שגיאה בטעינת המשימות')
    }
    setLoading(false)
  }, [account._id])

  useEffect(() => { load() }, [load])

  const commit = (next: CalendarTask[]) => {
    setTasks(next)
    onChanged(next.filter(t => t.status === 'open'))
  }

  // Open by day (undated last), then done, newest first.
  const sorted = useMemo(() => {
    const open = tasks.filter(t => t.status === 'open').sort((a, b) =>
      (a.due_date ? dayKey(a.due_date) : '9999').localeCompare(b.due_date ? dayKey(b.due_date) : '9999') || byTime(a, b))
    const done = tasks.filter(t => t.status === 'done').sort((a, b) =>
      String(b.resolved_at || b.created_at).localeCompare(String(a.resolved_at || a.created_at)))
    return {open, done}
  }, [tasks])

  const today = dayKey(new Date())
  const renderRow = (t: CalendarTask) => {
    const st = stateOf(t)
    const m = STATE_META[st]
    const expanded = openId === t._id
    const late = t.status === 'open' && t.due_date && dayKey(t.due_date) < today
    return (
      <div key={t._id} style={{border: `1px solid ${expanded ? m.color : '#eff2f5'}`, borderRadius: '10px', marginBottom: '8px', overflow: 'hidden'}}>
        <div
          onClick={() => setOpenId(expanded ? null : t._id)}
          style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', cursor: 'pointer', background: expanded ? m.bg : '#fff'}}
        >
          <i className={`bi ${m.icon}`} style={{color: m.color}}></i>
          <span style={{flex: 1, fontSize: '13px', textDecoration: st === 'done' ? 'line-through' : 'none', color: st === 'done' ? '#7e8299' : '#1e1e2d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
            {t.reason || <span style={{color: '#a1a5b7'}}>ללא תיאור</span>}
          </span>
          <span style={{fontSize: '11px', fontWeight: 700, color: late ? '#f1416c' : '#7e8299', whiteSpace: 'nowrap'}}>
            {t.due_date ? `${prettyDay(dayKey(t.due_date))}${t.due_time ? ' ' + t.due_time : ''}` : 'ללא תאריך'}
          </span>
          <span style={{fontSize: '10px', fontWeight: 700, color: m.color, background: m.bg, borderRadius: '6px', padding: '1px 6px'}}>{m.label}</span>
        </div>
        {expanded && (
          <div style={{borderTop: '1px solid #eff2f5'}}>
            <TaskEditor
              key={t._id}
              task={t}
              onChanged={u => commit(tasks.map(x => x._id === u._id ? u : x))}
              onDeleted={id => { commit(tasks.filter(x => x._id !== id)); setOpenId(null) }}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <Popup title={`משימות תשלום · ${name}`} onClose={onClose} width={560}>
      <div style={{padding: '14px 20px'}}>
        <NewTaskInline accountId={account._id} onCreated={task => commit([toCalendarTask(task), ...tasks])} />
        {error && <div style={{fontSize: '12px', color: '#f1416c', marginBottom: '8px'}}>{error}</div>}
        {loading ? (
          <div style={{textAlign: 'center', padding: '20px'}}><span className="spinner-border spinner-border-sm" style={{color: '#3699ff'}} /></div>
        ) : (
          <>
            {sorted.open.length === 0 && <div style={{fontSize: '12px', color: '#a1a5b7', marginBottom: '10px'}}>אין משימות פתוחות ללקוח הזה</div>}
            {sorted.open.map(renderRow)}
            {sorted.done.length > 0 && (
              <>
                <button onClick={() => setShowDone(v => !v)} style={{border: 'none', background: 'none', color: '#7e8299', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: '6px 0'}}>
                  <i className={`bi ${showDone ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{marginLeft: '4px'}}></i>
                  הושלמו ({sorted.done.length})
                </button>
                {showDone && sorted.done.map(renderRow)}
              </>
            )}
          </>
        )}
      </div>
    </Popup>
  )
}

// Header and optional footer stay put; only the body scrolls, so action
// buttons are always reachable however long the content gets.
const Popup: FC<{title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; z?: number; width?: number}> = ({title, onClose, children, footer, z = 1050, width = 460}) => {
  const isSmall = window.innerWidth <= 768
  return (
    <>
      <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: z - 10}} onClick={onClose} />
      <div style={{
        position: 'fixed',
        ...(isSmall
          ? {bottom: 0, left: 0, right: 0, borderRadius: '16px 16px 0 0', maxHeight: '88vh'}
          : {top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '14px', width: `${width}px`, maxWidth: '92vw', maxHeight: '84vh'}),
        background: '#fff', zIndex: z, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', direction: 'rtl',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{padding: '14px 20px', borderBottom: '1px solid #e4e6ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0}}>
          <h4 style={{margin: 0, fontSize: '15px', fontWeight: 700}}>{title}</h4>
          <button onClick={onClose} style={{border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#a1a5b7'}}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div style={{overflowY: 'auto', flex: '1 1 auto', minHeight: 0}}>{children}</div>
        {footer && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #e4e6ef', display: 'flex', gap: '8px', justifyContent: 'flex-end', flexShrink: 0, background: '#fff'}}>
            {footer}
          </div>
        )}
      </div>
    </>
  )
}

export default PaymentsCalendar
